import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * A custom hook to manage history for Undo/Redo functionality using generic state snapshots.
 * It stores up to maxDepth elements to prevent excessive memory usage.
 */
export function useMapHistory<T>(
  getState: () => T,
  setState: (state: T) => void,
  maxDepth = 5
) {
  const getStateRef = useRef(getState);
  const setStateRef = useRef(setState);
  
  useEffect(() => {
      getStateRef.current = getState;
      setStateRef.current = setState;
  }, [getState, setState]);

  const historyStack = useRef<T[]>([]);
  const historyIndex = useRef<number>(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateFlags = useCallback(() => {
    setCanUndo(historyIndex.current > 0);
    setCanRedo(historyIndex.current < historyStack.current.length - 1);
  }, []);

  const commitHistory = useCallback(() => {
    // We use setTimeout to ensure React state (e.g. placedSymbols) has flushed 
    // to the closure before taking the snapshot.
    setTimeout(() => {
        const currentState = getStateRef.current();
        
        // If we are not at the end of the stack (due to undos), truncate the redo future
        if (historyIndex.current < historyStack.current.length - 1) {
            historyStack.current = historyStack.current.slice(0, historyIndex.current + 1);
        }
        
        historyStack.current.push(currentState);
        
        if (historyStack.current.length > maxDepth + 1) {
          historyStack.current.shift();
        } else {
          historyIndex.current++;
        }
        updateFlags();
    }, 50);
  }, [maxDepth, updateFlags]);

  const undo = useCallback(() => {
    if (historyIndex.current > 0) {
        historyIndex.current--;
        setStateRef.current(historyStack.current[historyIndex.current]);
        updateFlags();
    }
  }, [updateFlags]);

  const redo = useCallback(() => {
    if (historyIndex.current < historyStack.current.length - 1) {
        historyIndex.current++;
        setStateRef.current(historyStack.current[historyIndex.current]);
        updateFlags();
    }
  }, [updateFlags]);

  const clearHistory = useCallback(() => {
    // Capture the initial state as the base of the history stack
    setTimeout(() => {
        historyStack.current = [getStateRef.current()];
        historyIndex.current = 0;
        updateFlags();
    }, 50);
  }, [updateFlags]);

  return {
    commitHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory
  };
}
